// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BlockCard from "@keller/App/Widgets/ContentBlock/BlockCard";
import systemColors from "@keller/XDL/PreStyle/colors";
import { xScreen } from "@keller/XDL/Utilities";
import omit from "lodash/omit";
import * as React from "react";
import { css } from "styled-components";

const enum blockCardItemFields {
    image = "image",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    imagePosition = "imagePosition",
    title = "title",
    titleColor = "titleColor",
    description = "description",
    descriptionColor = "descriptionColor",
    buttonVariant = "buttonVariant",
    buttonLabel = "buttonLabel",
    link = "link",
    backgroundColor = "backgroundColor",
}

interface BlockCardItemProps {
    fields: {
        [blockCardItemFields.image]: string;
        [blockCardItemFields.isMobileSpecific]?: boolean;
        [blockCardItemFields.mobileSpecificImage]?: string;
        [blockCardItemFields.imagePosition]: "left" | "right";
        [blockCardItemFields.title]: string;
        [blockCardItemFields.titleColor]: "black" | "white";
        [blockCardItemFields.description]: string;
        [blockCardItemFields.descriptionColor]: "black" | "white";
        [blockCardItemFields.buttonVariant]: "primary" | "secondary" | "tertiary" | "link";
        [blockCardItemFields.buttonLabel]: string;
        [blockCardItemFields.link]: LinkFieldValue;
        [blockCardItemFields.backgroundColor]: string;
    };
}

const enum fields {
    size = "size",
    blocks = "blocks",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.size]: "small" | "medium";
        [fields.blocks]: BlockCardItemProps[];
    };
}

type Props = OwnProps;

export interface ContentBlockStyles {
    container?: InjectableCss;
}

export const contentBlockStyles: ContentBlockStyles = {
    container: {
        css: css`
            position: relative;
            ${({ size }: any) =>
                size === "small" &&
                css`
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    & > div:not(:last-child) {
                        margin-right: 20px;
                    }

                    ${xScreen("lte", 1300)} {
                        flex-wrap: nowrap;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: center;
                        > div:not(:last-child) {
                            margin-right: 0;
                            margin-bottom: 20px;
                        }
                    }
                `}
        `,
    },
};

const styles = contentBlockStyles;

const ContentBlock: React.FC<Props> = ({ fields }) => {
    const { size } = fields;
    const blocks =
        size === "medium"
            ? ([{ fields: { ...omit(fields, ["blocks", "size"]) } }] as BlockCardItemProps[])
            : fields.blocks;

    return (
        <StyledWrapper {...styles.container} {...{ size }} data-test-selector="Keller-ContentBlock">
            {blocks.map(block => {
                return (
                    <BlockCard
                        key={`ContentBlock_${block.fields.title}`}
                        image={block.fields.image}
                        imagePosition={block.fields.imagePosition}
                        title={block.fields.title}
                        titleColor={block.fields.titleColor}
                        description={block.fields.description}
                        descriptionColor={block.fields.descriptionColor}
                        buttonVariant={block.fields.buttonVariant}
                        buttonLabel={block.fields.buttonLabel}
                        link={block.fields.link}
                        size={size}
                        mobileSpecificImage={block.fields.mobileSpecificImage}
                        backgroundColor={block.fields.backgroundColor}
                    />
                );
            })}
        </StyledWrapper>
    );
};

/**
 * in small case, we can't reach out to item.fields.size so we hide all the system-list if size = medium
 * in medium case, we check item.fields.size === medium
 * @param size
 */
const cardFieldDefinitions = (size: "small" | "medium"): FieldDefinition[] => {
    const showField = (fieldSize?: "small" | "medium") => {
        return size === "small" ? true : !!fieldSize && fieldSize === "medium";
    };

    const extraProps: any =
        size === "medium"
            ? {
                  fieldType: "General",
              }
            : {};

    return [
        {
            name: blockCardItemFields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.titleColor,
            displayName: "Title Color",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Black", value: "black" },
                { displayName: "White", value: "white" },
            ],
            hideEmptyOption: true,
            defaultValue: "black",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.description,
            displayName: "Description",
            editorTemplate: "RichTextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.descriptionColor,
            displayName: "Description Color",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Black", value: "black" },
                { displayName: "White", value: "white" },
            ],
            hideEmptyOption: true,
            defaultValue: "black",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.image,
            displayName: "Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.isMobileSpecific,
            displayName: "Mobile Specific Image",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            variant: "toggle",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.mobileSpecificImage,
            displayName: "Mobile Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isVisible: item => item.fields.isMobileSpecific && showField(item.fields.size),
            isRequired: true,
            ...extraProps,
        },
        {
            name: blockCardItemFields.imagePosition,
            displayName: "Image Position",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "left" },
                { displayName: "Right", value: "right" },
            ],
            hideEmptyOption: true,
            defaultValue: "right",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: (systemColors as any).common.ui10,
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.buttonLabel,
            editorTemplate: "TextField",
            displayName: "Button Label",
            defaultValue: "",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.link,
            displayName: "Button Link",
            editorTemplate: "LinkField",
            defaultValue: { value: "", type: "Url" },
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
        {
            name: blockCardItemFields.buttonVariant,
            displayName: "Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
                { displayName: "Link", value: "link" },
            ],
            hideEmptyOption: true,
            defaultValue: "primary",
            isVisible: item => showField(item.fields.size),
            ...extraProps,
        },
    ];
};

const widgetModule: WidgetModule = {
    component: ContentBlock,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.size,
                displayName: "Blocks Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Small", value: "small" },
                ],
                defaultValue: "medium",
                fieldType: "General",
                isRequired: true,
            },
            ...cardFieldDefinitions("medium"),
            {
                name: fields.blocks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.size === "small",
                fieldDefinitions: [...cardFieldDefinitions("small")],
            },
        ],
    },
};

export default widgetModule;
